import React, { useState } from "react";
import tw, { styled } from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import AnimationRevealPage from "helpers/AnimationRevealPage";

import VectorDesign from "images/Vector1.svg";
import GrayDesign from "images/blueDots.svg";
import LineDesign from "images/halfRedCircle.png";
import FullRed from "images/halfRedGray.svg";

// -----------------------------
// Styled Components
// -----------------------------

const Section = tw.section`
  bg-gradient-to-br from-white to-gray-100 py-20 px-4 relative overflow-hidden
`;

const Container = tw.div`
  max-w-6xl mx-auto relative z-10
`;

const HeadingWrap = tw.div`text-center mb-16`;

const PageTitle = tw.h1`
  text-4xl md:text-5xl font-bold text-gray-800 mb-4
`;

const Vector = tw.img`w-20 mx-auto mb-4`;

const SubText = tw.p`
  text-xl text-gray-600 max-w-2xl mx-auto
`;

const Box = tw.div`
  bg-white rounded-2xl shadow-lg overflow-hidden mb-16
`;

const BoxInner = tw.div`p-8 md:p-12`;

const SectionTitle = tw.h2`
  text-2xl font-bold text-gray-800 mb-4 border-b-2 border-gray-200 pb-2
`;

const Paragraph = tw.p`text-gray-700 leading-relaxed mb-4`;

const BulletList = tw.ul`space-y-2 text-gray-700 mb-4 list-decimal pl-6`;

const AlphaList = tw.ul`space-y-2 text-gray-700 mb-4`;

const ContactBox = tw.div`text-center mt-10`;

const ContactButton = tw.a`
  inline-block bg-[#3f6c68] hover:bg-[#355a56] text-white font-bold
  py-4 px-8 rounded-full text-lg transition-colors duration-300
  shadow-lg hover:shadow-xl
`;

const FloatingImg = styled.img(({ position }) => [
  tw`absolute opacity-75 transition-transform duration-200`,
  position === "left" && tw`top-[150px] left-0`,
  position === "right" && tw`bottom-[60px] right-[60px]`,
  position === "mid" && tw`top-1/2 right-[10%] opacity-50`,
]);

// -----------------------------
// Component
// -----------------------------

const RefundPolicySection = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const x = e.clientX - window.innerWidth / 2;
    const y = e.clientY - window.innerHeight / 2;
    setMouse({ x, y });
  };

  return (
    <AnimationRevealPage>
      <Header />

      <Section onMouseMove={handleMouseMove}>
        {/* Floating Decorative Images */}
        <FloatingImg
          src={GrayDesign}
          position="left"
          style={{
            transform: `translate(${mouse.x * 0.03}px, ${mouse.y * 0.03}px)`,
          }}
        />

        <FloatingImg
          src={LineDesign}
          position="right"
          style={{
            transform: `translate(${mouse.x * -0.03}px, ${mouse.y * -0.03}px)`,
          }}
        />

        <FloatingImg
          src={FullRed}
          position="mid"
          style={{
            transform: `translate(${mouse.x * -0.02}px, ${
              mouse.y * -0.02
            }px) rotate(45deg)`,
          }}
        />

        {/* MAIN CONTENT */}
        <Container>
          <HeadingWrap>
            <PageTitle>Refund & Return Policy</PageTitle>
            <Vector src={VectorDesign} />
            <SubText style={{ textAlign: "justify" }}>
              At{" "}
              <span style={{ fontWeight: 700, textAlign: "justify" }}>
                CA Vijender Aggarwal
              </span>
              , we are committed to providing high-quality educational and
              professional services. Please read our Refund & Return Policy
              carefully before making any purchase on our website.
            </SubText>
          </HeadingWrap>

          {/* ------------------ REFUND SECTION ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>1. No Refund Policy</SectionTitle>

              <Paragraph>
                All payments made for courses, study materials, digital content,
                consultancy services, or any other products/services offered on
                this website are{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  non-refundable.
                </span>
              </Paragraph>

              <Paragraph>
                Once an order is placed and payment is successfully processed,
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  no refund, cancellation, or reversal
                </span>{" "}
                will be permitted under any circumstances.
              </Paragraph>
            </BoxInner>
          </Box>

          {/* ------------------ RETURN CONDITIONS ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>2. No Return Policy</SectionTitle>

              <Paragraph>
                We do{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  not accept returns
                </span>{" "}
                for any products or services purchased through our website,
                including but not limited to:
              </Paragraph>

              <AlphaList>
                <li>
                  <b>A)</b> Online or offline courses
                </li>
                <li>
                  <b>B)</b> Recorded lectures
                </li>
                <li>
                  <b>C)</b> Study materials (printed or digital)
                </li>
                <li>
                  <b>D)</b> Consultancy or professional services
                </li>
              </AlphaList>
            </BoxInner>
          </Box>

          {/* ------------------ CANCELLATION POLICY ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>
                3. Replacement Policy (Only For Damaged Physical Products)
              </SectionTitle>

              <Paragraph>
                Replacement is applicable only in case the user receives a
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  physically damaged product
                </span>{" "}
                (if any physical material is provided).
              </Paragraph>

              <Paragraph>
                Replacement requests must meet the following conditions:
              </Paragraph>

              <BulletList>
                <li>
                  The issue must be reported{" "}
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    within 24 hours
                  </span>{" "}
                  of delivery.
                </li>
                <li>
                  The product must be{" "}
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    unused and in original condition.
                  </span>
                </li>
                <li>
                  The product must be returned with{" "}
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    {" "}
                    original packaging, labels, invoices, and accessories.
                  </span>
                </li>
                <li>
                  Damage caused due to misuse, mishandling, or normal wear and
                  tear will not be eligible for replacement.
                </li>
                <li>
                  Replacement requests are subject to{" "}
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    verification and approval{" "}
                  </span>
                  after inspection.
                </li>
              </BulletList>
            </BoxInner>
          </Box>

          {/* ------------------ TERMS & CONDITIONS ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>4. Reporting A Damaged Product</SectionTitle>

              <Paragraph>
                To request a replacement, please contact us within{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  24 hours of delivery
                </span>{" "}
                with:
              </Paragraph>

              <BulletList>
                <li>Order details</li>
                <li>Clear images of the damaged product</li>
                <li>A brief description of the issue</li>
              </BulletList>

              <Paragraph>
                Failure to report within the specified time may result in
                rejection of the request
              </Paragraph>
            </BoxInner>
          </Box>
          {/* Contact Support */}
          <Box>
            <BoxInner>
              <SectionTitle>5. Shipping Responsibility</SectionTitle>

              <Paragraph>
                Customers are required to send the product back using a{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  reputed courier service.
                </span>{" "}
                Proper packaging must be ensured to avoid further damage during
                transit.
              </Paragraph>
            </BoxInner>
          </Box>

          <Box>
            <BoxInner>
              <SectionTitle>6. Final Decision</SectionTitle>

              <Paragraph>
                All replacement requests are subject to internal verification.
                The decision of management at{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  {" "}
                  CA Vijender Aggarwal Classes shall be final and binding{" "}
                </span>{" "}
                in all cases.
              </Paragraph>
            </BoxInner>
          </Box>
        </Container>
      </Section>

      <Footer />
    </AnimationRevealPage>
  );
};

export default RefundPolicySection;
